import { PermissionVerbsV1, PermissionVerbsV2 } from '@innovation-os-ng/core/core';
import { Space, Tenant } from '@innovation-os-ng/core/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AuthContextSelectors } from '../auth-context/auth-context.selectors';
import { SPACE_FEATURE_SELECTOR } from '../constants';
import { SpaceState } from '../interfaces/space-state.interface';
import { TenantSelectors } from '../tenant/tenant.selectors';



const selectSpacesFeature = createFeatureSelector<SpaceState>(SPACE_FEATURE_SELECTOR);
const selectSpaces = createSelector(selectSpacesFeature, (state: SpaceState) => state.spaces);
const selectFlatSpacess = createSelector(selectSpaces, (spaces: { [tenantUri: string]: Array<Space> }) =>
  Object.entries(spaces)
    .map((_) => _[1])
    .flat()
);
const selectCurrentSpaceUri = createSelector(selectSpacesFeature, (state: SpaceState) => state?.currentSpaceUri);
const selectCurrentSpace = createSelector(selectSpacesFeature, (state: SpaceState) => state?.currentSpace);

const selectSpacesLoading = createSelector(selectSpacesFeature, (state: SpaceState) => state.isLoading);
const selectSpaceCreating = createSelector(selectSpacesFeature, (state: SpaceState) => state.isCreating);
const selectSpaceUpdating = createSelector(selectSpacesFeature, (state: SpaceState) => state.isUpdating);
const selectSpaceDeleting = createSelector(selectSpacesFeature, (state: SpaceState) => state.isDeleting);

const selectSpaceCreated = createSelector(selectSpacesFeature, (state: SpaceState) => state.result.created);
const selectSpaceDeleted = createSelector(selectSpacesFeature, (state: SpaceState) => state.result.deleted);
const selectSpaceUpdated = createSelector(selectSpacesFeature, (state: SpaceState) => state.result.updated);
const selectSpaceLoaded = createSelector(selectSpacesFeature, (state: SpaceState) => state.result.loaded);
const selectManySpacesLoaded = createSelector(selectSpacesFeature, (state: SpaceState) => state.result.collectionLoaded);

const selectCRUDResults = createSelector(selectSpacesFeature, (state: SpaceState) => state.result);

const selectSpaceByUri = (spaceUri: string) =>
  createSelector(selectFlatSpacess, (spaces: Array<Space>) => spaces.find((space) => space.uri === spaceUri));

const selectSpacesByTenantUri = (tenantUri: string) =>
  createSelector(selectSpaces, (spaces: { [tenantUri: string]: Array<Space> }) => spaces[tenantUri]);

const selectSpacesByCurrentTenant = createSelector(
  selectSpaces,
  TenantSelectors.selectCurrentTenant,
  (spaces: { [tenantUri: string]: Array<Space> }, tenant: Tenant) => {
    return tenant ? spaces[tenant.tenantUri] : null;
  }
);

const selectSpacesByTenantSlug = (tenantSlug: string) =>
  createSelector(
    selectSpaces,
    TenantSelectors.selectTenantBySlug(tenantSlug),
    (spaces: { [tenantUri: string]: Array<Space> }, tenant: Tenant) => spaces[tenant.tenantUri]
  );


const selectSpacesByAnyPermissions = (permissions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>) =>
  createSelector(AuthContextSelectors.selectSpaceUrisByAnyPermissions(permissions), selectFlatSpacess, (uris: Array<string>, spaces: Array<Space>) => {
    return spaces.filter((space) => uris.includes(space.uri));
  });

export const SpaceSelectors = {
  selectSpaces,
  selectCurrentSpaceUri,
  selectCurrentSpace,
  selectSpacesLoading,
  selectSpaceCreating,
  selectSpaceUpdating,
  selectSpaceDeleting,
  selectSpaceCreated,
  selectSpaceDeleted,
  selectSpaceUpdated,
  selectSpaceLoaded,
  selectManySpacesLoaded,
  selectCRUDResults,
  selectSpaceByUri,
  selectSpacesByTenantUri,
  selectSpacesByTenantSlug,
  selectSpacesByCurrentTenant,
  selectSpacesByAnyPermissions
};
